<script setup lang="ts">
/**
 * One setting of `NxSettingsList` („Einstellungen", I-2510): its title, what
 * it does, where its value comes from — the edited layer's own value („Eigener
 * Wert"), one a layer before it gives („Geerbt von …"), the default
 * („Standard") or a lock of a layer before it („Gesperrt von …", read-only) —
 * and the kit control on the right (an `order` is a sortable list below the
 * text, its value the option values joined by `,`). „Zurücksetzen" (↺)
 * removes the layer's own value; on an administrator's layer the lock locks
 * the field for every layer after it.
 *
 * A `keybinding` row is a select of presets (`options`) with a last entry
 * „Eigenes Kürzel …“: picking it swaps the select for a recorder
 * (`NxKeybindingInput`, checked by the item's `validate`) that takes the focus
 * but stays idle — Enter, Space or a click starts recording, so the arrow keys
 * of the select (Windows fires `change` on them) can never be recorded. A
 * recorded combo is emitted as `change`, like a picked preset, and the select
 * comes back; Escape or leaving the recorder brings it back unchanged. A stored
 * combo that `validate` refuses (another platform's, an older rule) is shown
 * as it is, with the reason under the control as a quiet warning.
 *
 * Presentational: it emits `change` with the new value, `reset` and `lock`;
 * the host writes them.
 */
import { ArrowUndoIcon, LockClosedIcon } from '@blpde/nx-icons';
import { computed, inject, nextTick, ref, useId, useTemplateRef, watch } from 'vue';
import NxIconButton from '../IconButton/NxIconButton.vue';
import NxNativeSelect from '../Inputs/NxNativeSelect.vue';
import NxNumberInput, {
  FLOAT_PATTERN,
  INT_PATTERN,
  UINT_PATTERN,
} from '../Inputs/NxNumberInput.vue';
import NxSwitch from '../Inputs/NxSwitch.vue';
import NxTextInput from '../Inputs/NxTextInput.vue';
import { i18nInjectionKey } from '../internal';
import { formatCombo } from '../Shortcuts/keys';
import NxKeybindingInput from '../Shortcuts/NxKeybindingInput.vue';
import NxSortableList from '../Sortable/NxSortableList.vue';
import NxToggleGroup from '../ToggleGroup/NxToggleGroup.vue';
import {
  nxSettingsNumber,
  type NxSettingsControlSlot,
  type NxSettingsListItem,
  type NxSettingsListOption,
} from './settingsList';

const props = withDefaults(
  defineProps<{
    item: NxSettingsListItem;
    /** Nothing can be changed (the whole list is read-only). */
    disabled?: boolean;
    /** Control height: `s` on pages, `xs` in narrow inspectors. */
    height?: 'xs' | 's';
    /** A narrow host: the control goes below the text (a switch stays on the right). */
    compact?: boolean;
    /** The control's id (the title is its label). */
    controlId?: string;
  }>(),
  { disabled: false, height: 's', compact: false, controlId: undefined }
);

const emit = defineEmits<{
  /** The new value the user picked. */
  change: [value: unknown];
  /** Remove the edited layer's own value. */
  reset: [];
  /** Lock (or free) the field for every layer after the edited one. */
  lock: [locked: boolean];
}>();

defineSlots<{
  /** Below the description — a link to a page that goes deeper. */
  after?: () => unknown;
  /** For `control: 'custom'`: bind data using kit controls, with the row's guards. */
  control?: (props: NxSettingsControlSlot) => unknown;
}>();

const i18n = inject(i18nInjectionKey, null);

function text(key: string, params?: Record<string, unknown>): string {
  const id = `common.settings_list.${key}`;
  if (!i18n) return id;
  return params ? i18n.t(id, params) : i18n.t(id);
}

/** A choice of no value has no string to carry: segments and selects run on this token. */
const NONE = '\u0001';
/** Matches no choice: a value that is no option shows no segment selected. */
const UNSET = '\u0000';

const editable = computed(
  () => !props.disabled && !props.item.disabled && props.item.origin !== 'locked'
);

function commitCustom(value: unknown): void {
  if (editable.value && props.item.control === 'custom') emit('change', value);
}

const originText = computed(() => {
  const { origin, source } = props.item;
  switch (origin) {
    case 'own':
      return text('own');
    case 'locked':
      return source ? text('locked_by', { source }) : text('locked');
    case 'inherited':
      return source ? text('inherited_from', { source }) : text('inherited');
    default:
      return text('default');
  }
});

const lockTitle = computed(() => {
  const field = props.item.label;
  const main = props.item.locked ? text('unlock', { field }) : text('lock', { field });
  const also = props.item.lockAlso ?? [];
  return also.length ? `${main} ${text('lock_also', { fields: also.join(', ') })}` : main;
});

/* ── Choices ──────────────────────────────────────────────────────────── */

/** A number the host computed may be off by a rounding error. */
function sameValue(a: unknown, b: unknown): boolean {
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) <= 1e-9;
  return a === b;
}

const options = computed<readonly NxSettingsListOption[]>(() => props.item.options ?? []);

/** The option the value is: an unset value is the option of no value, where there is one. */
const optionIndex = computed(() => {
  const value = props.item.value;
  if (value === undefined || value === null) {
    return options.value.findIndex((option) => option.value === null);
  }
  return options.value.findIndex((option) => sameValue(option.value, value));
});

function tokenOf(option: NxSettingsListOption): string {
  return option.value === null ? NONE : String(option.value);
}

const toggleOptions = computed(() =>
  options.value.map((option) => ({ value: tokenOf(option), label: option.label }))
);

const toggleModel = computed(() =>
  optionIndex.value < 0 ? UNSET : tokenOf(options.value[optionIndex.value]!)
);

const isKeybinding = computed(() => props.item.control === 'keybinding');

/**
 * A stored value that is no option (any more) stays visible in the select; a
 * keybinding that is no preset reads as the combo it is („⌘⇧F“).
 */
const selectChoices = computed<readonly NxSettingsListOption[]>(() => {
  const value = props.item.value;
  if (optionIndex.value >= 0 || (typeof value !== 'string' && typeof value !== 'number')) {
    return options.value;
  }
  const label = isKeybinding.value ? formatCombo(String(value)) : text('other_value', { value });
  return [...options.value, { value, label }];
});

/**
 * The select runs on positions: an option may be `''` or no value at all. A
 * keybinding ends in „Eigenes Kürzel …“, one position past the choices.
 */
const selectOptions = computed(() => {
  const choices = selectChoices.value.map((choice, index) => ({
    value: index,
    label: choice.label,
  }));
  return isKeybinding.value
    ? [...choices, { value: choices.length, label: text('custom_keybinding') }]
    : choices;
});

const selectModel = computed(() => {
  const value = props.item.value;
  const index = selectChoices.value.findIndex((choice) =>
    value === undefined || value === null ? choice.value === null : sameValue(choice.value, value)
  );
  return index < 0 ? null : index;
});

function pick(option: NxSettingsListOption | undefined): void {
  if (!option) return;
  if (option.value === null) {
    if (props.item.origin === 'own') emit('reset');
    return;
  }
  if (sameValue(option.value, props.item.value)) return;
  emit('change', option.value);
}

function pickSegment(token: string | number): void {
  if (token === UNSET) return;
  pick(options.value.find((option) => tokenOf(option) === String(token)));
}

function pickIndex(index: string | number | null): void {
  if (typeof index !== 'number') return;
  if (isKeybinding.value && index === selectChoices.value.length) {
    recording.value = true;
    return;
  }
  pick(selectChoices.value[index]);
}

/* ── Keybinding recorder ──────────────────────────────────────────────── */

/** „Eigenes Kürzel …“ was picked: the recorder stands where the select was. */
const recording = ref(false);
const controlCell = useTemplateRef<HTMLElement>('control-cell');

/** The select comes back; the keyboard keeps its place when the recorder had the focus. */
async function leaveRecorder(): Promise<void> {
  const hadFocus = !!controlCell.value?.contains(document.activeElement);
  recording.value = false;
  if (!hadFocus) return;
  await nextTick();
  controlCell.value?.querySelector('select')?.focus();
}

function recorded(combo: string): void {
  if (combo !== props.item.value) emit('change', combo);
  void leaveRecorder();
}

// A recorder that is no longer allowed (locked, read-only) or belongs to another row goes away.
watch(
  () => [editable.value, props.item.id] as const,
  () => {
    recording.value = false;
  }
);

/** Why the stored combo does not work on this device (the reason `validate` gives), if it does not. */
const warning = computed(() => {
  const value = props.item.value;
  if (!isKeybinding.value || typeof value !== 'string' || value === '') return null;
  const reason = props.item.validate?.(value);
  return reason ? text('not_active_here', { reason }) : null;
});

const warningId = `${useId()}-warning`;

/* ── Order ────────────────────────────────────────────────────────────── */

/** The separator an `order` row joins its option values with. */
const ORDER_SEPARATOR = ',';

/**
 * The options in the order of the row's value (the option values joined by
 * `,`): unknown parts and repeats are ignored, options the value lacks follow
 * in option order.
 */
const orderItems = computed(() => {
  const known = new Map<string, NxSettingsListOption>();
  for (const option of options.value) {
    if (typeof option.value === 'string') known.set(option.value, option);
  }
  const parts = typeof props.item.value === 'string' ? props.item.value.split(ORDER_SEPARATOR) : [];
  const sorted: NxSettingsListOption[] = [];
  for (const part of [...parts, ...known.keys()]) {
    const option = known.get(part);
    if (option && !sorted.includes(option)) sorted.push(option);
  }
  return sorted;
});

function pickOrder(next: NxSettingsListOption[]): void {
  const joined = next.map((option) => option.value).join(ORDER_SEPARATOR);
  if (joined === orderItems.value.map((option) => option.value).join(ORDER_SEPARATOR)) return;
  emit('change', joined);
}

/* ── Switch, number, text ─────────────────────────────────────────────── */

function toggle(next: boolean): void {
  if (next !== (props.item.value === true)) emit('change', next);
}

/** Bumped to redraw an input whose typed text was refused. */
const redraw = ref(0);

const numberPattern = computed(() => {
  const step = props.item.step;
  if (step === undefined || !Number.isInteger(step)) return FLOAT_PATTERN;
  return props.item.min !== undefined && props.item.min >= 0 ? UINT_PATTERN : INT_PATTERN;
});

const numberModel = computed(() => (typeof props.item.value === 'number' ? props.item.value : ''));

function commitNumber(raw: number | string): void {
  const next = nxSettingsNumber(raw, props.item);
  if (next === null) {
    if (String(raw).trim() === '' && props.item.origin === 'own') emit('reset');
    else redraw.value += 1;
    return;
  }
  if (sameValue(next, props.item.value)) {
    redraw.value += 1;
    return;
  }
  emit('change', next);
}

const textModel = computed(() => (typeof props.item.value === 'string' ? props.item.value : ''));

function commitText(raw: string): void {
  const next = raw.trim();
  if (!next) {
    if (props.item.origin === 'own') emit('reset');
    else redraw.value += 1;
    return;
  }
  if (next === props.item.value) return;
  emit('change', props.item.maxLength ? next.slice(0, props.item.maxLength) : next);
}

/** A segmented control and a sortable list carry the title as their name (no single field to label). */
const labelled = computed(() => props.item.control !== 'choice' && props.item.control !== 'order');
</script>

<template>
  <div
    class="nx-settings-row"
    :class="[
      `is-${item.origin}`,
      `control-${item.control}`,
      { 'is-compact': compact, 'is-lock-set': item.locked },
    ]"
    data-testid="settings-row"
    :data-item="item.id"
  >
    <div class="nx-settings-row-text">
      <label v-if="labelled" class="nx-settings-row-title" :for="controlId">{{ item.label }}</label>
      <span v-else class="nx-settings-row-title">{{ item.label }}</span>
      <p v-if="item.description" class="nx-settings-row-description">{{ item.description }}</p>
      <slot name="after" />
      <div class="nx-settings-row-meta">
        <span class="nx-settings-row-origin" data-testid="settings-row-origin">
          <LockClosedIcon
            v-if="item.origin === 'locked'"
            filled
            class="nxt-icon-xs"
            aria-hidden="true"
          />
          {{ originText }}
        </span>
        <NxIconButton
          v-if="item.origin === 'own' && editable"
          height="xs"
          data-testid="settings-row-reset"
          :title="text('reset', { field: item.label })"
          @click="emit('reset')"
        >
          <ArrowUndoIcon class="nxt-icon-sm" />
        </NxIconButton>
        <NxIconButton
          v-if="item.lockable && item.origin !== 'locked'"
          height="xs"
          data-testid="settings-row-lock"
          :pressed="item.locked === true"
          :disabled="disabled || item.disabled"
          :title="lockTitle"
          @click="emit('lock', !item.locked)"
        >
          <LockClosedIcon :filled="item.locked === true" class="nxt-icon-sm" />
        </NxIconButton>
        <span v-if="item.locked" class="nx-settings-row-locks-here">{{ text('locks_here') }}</span>
      </div>
    </div>

    <div ref="control-cell" class="nx-settings-row-control">
      <slot
        v-if="item.control === 'custom'"
        name="control"
        :item="item"
        :control-id="controlId"
        :editable="editable"
        :height="height"
        :on-change="commitCustom"
      />
      <NxSwitch
        v-else-if="item.control === 'switch'"
        :id="controlId"
        :model-value="item.value === true"
        :disabled="!editable"
        height="xs"
        @update:model-value="toggle"
      />
      <NxToggleGroup
        v-else-if="item.control === 'choice'"
        :model-value="toggleModel"
        :options="toggleOptions"
        :disabled="!editable"
        :aria-label="item.label"
        :height="height"
        wrap
        @update:model-value="pickSegment"
      />
      <NxKeybindingInput
        v-else-if="item.control === 'keybinding' && recording"
        :id="controlId"
        model-value=""
        :validate="item.validate"
        size="full"
        autofocus
        @update:model-value="recorded"
        @cancel="leaveRecorder"
      />
      <NxSortableList
        v-else-if="item.control === 'order'"
        class="nx-settings-row-order"
        :items="orderItems"
        item-key="value"
        :label-of="(option: NxSettingsListOption) => option.label"
        :aria-label="item.label"
        :disabled="!editable"
        @update:items="pickOrder"
      >
        <template #item="{ item: option }">{{ option.label }}</template>
      </NxSortableList>
      <NxNativeSelect
        v-else-if="item.control === 'select' || item.control === 'keybinding'"
        :id="controlId"
        :model-value="selectModel"
        :options="selectOptions"
        :placeholder="text('choose')"
        :disabled="!editable"
        :aria-describedby="warning ? warningId : undefined"
        size="full"
        @update:model-value="pickIndex"
      />
      <span v-else-if="item.control === 'number'" class="nx-settings-row-number">
        <NxNumberInput
          :id="controlId"
          :key="redraw"
          :model-value="numberModel"
          :model-modifiers="{ lazy: true }"
          :pattern="numberPattern"
          :height="height"
          :disabled="!editable"
          inputmode="decimal"
          @update:model-value="commitNumber"
        />
        <span v-if="item.unit" class="nx-settings-row-unit">{{ item.unit }}</span>
      </span>
      <NxTextInput
        v-else
        :id="controlId"
        :key="redraw"
        :model-value="textModel"
        :model-modifiers="{ lazy: true }"
        :height="height"
        :disabled="!editable"
        :maxlength="item.maxLength || undefined"
        @update:model-value="commitText"
      />
      <!-- After the control chain: an element between its branches would end the
           v-else-if chain and render the text input under every other control. -->
      <p
        v-if="warning && !recording"
        :id="warningId"
        class="nx-settings-row-warning"
        data-testid="settings-row-warning"
      >
        {{ warning }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Text left, control right (a switch, a choice, a select, a number); a narrow
   host moves every control but the switch below the text. */
.nx-settings-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, max-content);
  align-items: start;
  gap: var(--nxt-space-2xs) var(--nxt-space-md);
  min-width: 0;
  padding: var(--nxt-space-sm) 0;
}

.nx-settings-row-text {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}

.nx-settings-row-title {
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  line-height: var(--nxt-line-height-tight);
  overflow-wrap: anywhere;
}

/* What the setting does: readable line length, quiet colour. */
.nx-settings-row-description {
  max-width: 68ch;
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}

.nx-settings-row-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 var(--nxt-space-2xs);
  min-height: var(--nxt-control-size-xs);
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
}

.nx-settings-row-origin {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
}

/* The edited layer's own value takes the brand colour, a lock the warning one. */
.is-own .nx-settings-row-origin {
  color: var(--nxt-brand-subtle-fg);
}

.is-locked .nx-settings-row-origin,
.nx-settings-row-locks-here {
  color: var(--nxt-warning-subtle-fg);
}

.nx-settings-row-control {
  display: flex;
  justify-content: flex-end;
  min-width: 0;
  max-width: calc(var(--nxt-size-unit) * 26);
  padding-top: var(--nxt-space-2xs);
}

/* A select (and a keybinding, the same select or its recorder) fills its column up to a readable width. */
.control-select .nx-settings-row-control,
.control-keybinding .nx-settings-row-control {
  width: calc(var(--nxt-size-unit) * 16);
}

/* A keybinding row's cell is a column: the control, then the warning under it. */
.control-keybinding .nx-settings-row-control {
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}

.nx-settings-row-warning {
  margin: 0;
  color: var(--nxt-warning-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
  overflow-wrap: anywhere;
}

/* A list of options needs the width: below the text, never beside it. */
.control-order {
  grid-template-columns: minmax(0, 1fr);
}

.control-order .nx-settings-row-control {
  justify-content: flex-start;
  width: 100%;
  max-width: calc(var(--nxt-size-unit) * 26);
  padding-top: 0;
}

.nx-settings-row-order {
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--nxt-font-size-sm);
}

.nx-settings-row-number {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
}

.nx-settings-row-number > :first-child {
  width: calc(var(--nxt-size-unit) * 6);
}

.nx-settings-row-unit {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}

.is-compact {
  grid-template-columns: minmax(0, 1fr);
}

.is-compact .nx-settings-row-control {
  justify-content: flex-start;
  max-width: none;
  padding-top: 0;
}

.is-compact.control-select .nx-settings-row-control,
.is-compact.control-keybinding .nx-settings-row-control {
  width: 100%;
}

/* A switch stays beside the title, also in a narrow host. */
.is-compact.control-switch {
  grid-template-columns: minmax(0, 1fr) auto;
}

.is-compact.control-switch .nx-settings-row-control {
  padding-top: 0;
}

@media (max-width: 40em) {
  .nx-settings-row:not(.control-switch) {
    grid-template-columns: minmax(0, 1fr);
  }

  .nx-settings-row:not(.control-switch) .nx-settings-row-control {
    justify-content: flex-start;
    max-width: none;
  }
}
</style>
